
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Star } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface SessionFeedbackFormProps {
  sessionId: string;
  sessionType: string;
  onFeedbackSubmitted: () => void;
}

const SessionFeedbackForm = ({ sessionId, sessionType, onFeedbackSubmitted }: SessionFeedbackFormProps) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (rating === 0) {
      toast({
        title: "Rating required",
        description: "Please provide a rating before submitting feedback.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("User not authenticated");

      // Insert feedback
      const { error: feedbackError } = await supabase
        .from('session_feedback')
        .insert({
          session_id: sessionId,
          user_id: user.id,
          rating,
          feedback_text: feedbackText.trim() || null
        });

      if (feedbackError) throw feedbackError;

      // Update session to mark feedback as submitted
      const { error: sessionError } = await supabase
        .from('candidate_sessions')
        .update({ feedback_submitted: true })
        .eq('id', sessionId);

      if (sessionError) throw sessionError;

      toast({
        title: "Feedback submitted",
        description: "Thank you for your feedback! It helps improve our coaching sessions."
      });

      onFeedbackSubmitted();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast({
        title: "Error submitting feedback",
        description: "There was a problem submitting your feedback. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="mt-4">
      <CardHeader>
        <CardTitle className="text-lg">Share Your Feedback</CardTitle>
        <p className="text-sm text-muted-foreground">
          How was your {sessionType.toLowerCase().replace('_', ' ')} session? Your feedback helps us improve.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <label className="text-sm font-medium mb-2 block">Rating *</label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                onClick={() => setRating(star)}
                onMouseEnter={() => setHoveredRating(star)}
                onMouseLeave={() => setHoveredRating(0)}
                className="p-1 rounded hover:bg-muted transition-colors"
              >
                <Star
                  className={`h-6 w-6 ${
                    star <= (hoveredRating || rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-muted-foreground'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="text-sm font-medium mb-2 block">Additional Comments (Optional)</label>
          <Textarea
            placeholder="Share your thoughts about the session, what was helpful, or suggestions for improvement..."
            value={feedbackText}
            onChange={(e) => setFeedbackText(e.target.value)}
            rows={4}
          />
        </div>

        <Button 
          onClick={handleSubmit} 
          disabled={isSubmitting || rating === 0}
          className="w-full"
        >
          {isSubmitting ? "Submitting..." : "Submit Feedback"}
        </Button>
      </CardContent>
    </Card>
  );
};

export default SessionFeedbackForm;
